import { type FC, useEffect, useRef, useState } from "react";

import { usePushNotifications } from "@application/hooks/use_push_notifications";

import { Button } from "@styled/button";
import { Notification } from "@styled/notification";
import { Sentence } from "@styled/sentence";

export const ProjectNotificationBanner: FC = () => {
  const { permissionState, loading, enable, supported, enabled } =
    usePushNotifications();
  // The "enabled" confirmation is transient feedback, never persistent chrome:
  // it shows only when the user clicks Enable here (auto-dismissing), and not on
  // an already-enabled load nor when the permission resolves async on mount — so
  // it can never become a standing layout row above the case detail.
  const [justEnabled, setJustEnabled] = useState(false);
  const requestedEnable = useRef(false);
  const wasEnabled = useRef(enabled);

  useEffect(() => {
    if (enabled && !wasEnabled.current && requestedEnable.current) {
      setJustEnabled(true);
      const timeout = setTimeout(() => setJustEnabled(false), 3000);
      wasEnabled.current = enabled;
      return () => clearTimeout(timeout);
    }
    wasEnabled.current = enabled;
  }, [enabled]);

  const handleEnable = () => {
    requestedEnable.current = true;
    enable();
  };

  if (!supported) return null;

  if (permissionState === "denied") {
    return (
      <Notification color="slate">
        <Sentence>
          Desktop notifications are blocked in your browser settings.
        </Sentence>
      </Notification>
    );
  }

  if (enabled) {
    if (!justEnabled) return null;
    return (
      <Notification color="emerald">
        <Sentence>Desktop notifications enabled</Sentence>
      </Notification>
    );
  }

  return (
    <Notification color="indigo">
      <Sentence>Get notified when documents finish processing</Sentence>
      <Button color="indigo" size="sm" onClick={handleEnable} loading={loading}>
        Enable
      </Button>
    </Notification>
  );
};
